import { EmptyState } from '@/components/primitives/EmptyState'
import type { DomainProfile } from '@/lib/scraper'
import { scraperApi } from '@/lib/scraper'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  BTN_PRIMARY,
  CARD,
  INPUT,
  LABEL,
  MUTED,
  PILL,
  SKELETON,
  cls
} from './ui'

const TIERS: DomainProfile['preferred_tier'][] = ['http', 'dynamic', 'stealth']

export function DomainsPanel() {
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const domains = useQuery({
    queryKey: ['scraper-domains', q],
    queryFn: () => scraperApi.domains(q)
  })

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['scraper-domains'] })

  const add = async (event: React.FormEvent) => {
    event.preventDefault()
    setAdding(true)
    try {
      await scraperApi.createDomain({ domain: newDomain })
      setNewDomain('')
      toast.success('Domain added')
      await refresh()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not add the domain'
      )
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label htmlFor="domain-search" className={LABEL}>
            Search
          </label>
          <input
            id="domain-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="yelp.com"
            className={INPUT}
          />
        </div>
        <form onSubmit={add}>
          <label htmlFor="domain-new" className={LABEL}>
            Add a domain (to pin a tier or mark it do not scrape)
          </label>
          <div className="flex gap-2">
            <input
              id="domain-new"
              required
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className={INPUT}
            />
            <button
              type="submit"
              disabled={adding}
              aria-label="Add domain"
              className={BTN_PRIMARY}
            >
              {adding ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Plus className="size-4" />
              )}
            </button>
          </div>
        </form>
      </div>
      {domains.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={cls(SKELETON, 'h-20')} />
          ))}
        </div>
      ) : domains.isError ? (
        <p className="text-sm text-red-500">Could not load domains.</p>
      ) : !domains.data?.results.length ? (
        <EmptyState
          icon={Globe}
          title="No domains yet"
          description="Every site the scraper visits gets a profile here: which tier works, how often it blocks, which captchas it shows."
          className="rounded-[36px] border-neutral-200 dark:border-neutral-800"
        />
      ) : (
        <ul className="space-y-2">
          {domains.data.results.map((d) => (
            <DomainRow key={d.id} domain={d} onSaved={refresh} />
          ))}
        </ul>
      )}
    </div>
  )
}

function DomainRow({
  domain,
  onSaved
}: { domain: DomainProfile; onSaved: () => void }) {
  const [saving, setSaving] = useState(false)
  const save = async (patch: Partial<DomainProfile>) => {
    setSaving(true)
    try {
      await scraperApi.updateDomain(domain.id, patch)
      onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }
  const captchas = Object.entries(domain.captcha_kinds ?? {})
  return (
    <li className={cls(CARD, 'flex flex-wrap items-center gap-3 py-4')}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-neutral-900 dark:text-white">
          {domain.domain}
        </p>
        <p className={MUTED}>
          {domain.success_count} ok · {domain.block_count} blocked
          {domain.last_block_at &&
            ` · last block ${new Date(domain.last_block_at).toLocaleDateString()}`}
        </p>
        {captchas.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {captchas.map(([kind, n]) => (
              <span key={kind} className={PILL}>
                {kind} × {n}
              </span>
            ))}
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
        Tier
        <select
          value={domain.preferred_tier}
          disabled={saving}
          onChange={(e) =>
            save({
              preferred_tier: e.target.value as DomainProfile['preferred_tier']
            })
          }
          className="rounded-[36px] border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
        >
          {TIERS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
        <input
          type="checkbox"
          checked={domain.pinned}
          disabled={saving}
          onChange={(e) => save({ pinned: e.target.checked })}
          className="size-4 accent-neutral-900 dark:accent-white"
        />
        Pin tier
      </label>
      <label className="flex items-center gap-2 text-xs font-semibold text-red-600 dark:text-red-400">
        <input
          type="checkbox"
          checked={domain.do_not_scrape}
          disabled={saving}
          onChange={(e) => save({ do_not_scrape: e.target.checked })}
          className="size-4 accent-red-500"
        />
        Do not scrape
      </label>
      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
        Gap (ms)
        <input
          type="number"
          min={0}
          defaultValue={domain.min_delay_ms}
          disabled={saving}
          onBlur={(e) =>
            Number(e.target.value) !== domain.min_delay_ms &&
            save({ min_delay_ms: Number(e.target.value) })
          }
          className="w-24 rounded-[36px] border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
        />
      </label>
      {saving && <Loader2 className="size-4 animate-spin text-neutral-400" />}
    </li>
  )
}
